import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightIcon, DatabaseIcon, FileTextIcon, SettingsIcon, ShieldCheckIcon, UserCheckIcon, ClipboardListIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { trackEvent as track } from "../../utils/analytics";
import { FitScale } from "../digital/FitScale";
import { TRUST_HERO_CITY, trustHeroCopy as c, trustSignals, Bi, TrustSignalKey } from "../../data/trustHero";
import { TrustLaptop, TrustPhone } from "./TrustDevices";
const EASE = [0.23, 1, 0.32, 1] as const;
const SIGNAL_ICONS: Record<TrustSignalKey, BoxIcon> = {
  sources: FileTextIcon,
  rules: ShieldCheckIcon,
  data: DatabaseIcon,
  systems: SettingsIcon,
  oversight: UserCheckIcon,
  audit: ClipboardListIcon
};

/* Positions on the 820 × 520 design canvas */
const SIGNAL_POS: Record<TrustSignalKey, {
  left: number;
  top: number;
}> = {
  sources: {
    left: 132,
    top: 8
  },
  rules: {
    left: 86,
    top: 176
  },
  data: {
    left: 26,
    top: 314
  },
  systems: {
    left: 722,
    top: 8
  },
  oversight: {
    left: 726,
    top: 176
  },
  audit: {
    left: 730,
    top: 330
  }
};
function SignalCard({
  k,
  label
}: {
  k: TrustSignalKey;
  label: string;
}) {
  const Icon = SIGNAL_ICONS[k];
  return <div className="w-[86px] rounded-2xl bg-white/95 border border-[#ECEBF7] shadow-[0_12px_28px_-18px_rgba(79,70,229,0.45)] px-2 pt-3 pb-2.5 flex flex-col items-center text-center" data-id="element-7010" data-mp="3p1ymtzh1kxhb">
      <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-7011" data-mp="2xaiurpwpuijp" />
      <span className="mt-2 font-sans text-[12px] leading-tight text-[#121A3A]" data-id="element-7012" data-mp="3b15spfunk4eq">{label}</span>
    </div>;
}
function HeroComposition({
  pick,
  lang
}: {
  pick: (b: Bi) => string;
  lang: 'FR' | 'EN';
}) {
  return <div className="relative w-[820px] h-[520px]" data-id="element-7013" data-mp="2ijjowmn9m83t">
      {/* City environment — secondary, faded into the page */}
      <div className="absolute left-[110px] right-0 top-[40px] bottom-[20px] overflow-hidden" aria-hidden="true" data-id="element-7014" data-mp="2dkqi1d2hhfcm">
        <img src={TRUST_HERO_CITY} alt="" className="w-full h-full object-cover opacity-85" data-id="element-7015" data-mp="hi3p70pidpdo" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,#fff_0%,rgba(255,255,255,0.7)_28%,rgba(255,255,255,0.08)_65%,rgba(255,255,255,0)_100%)]" data-id="element-7016" data-mp="mflzovj6ccns" />
        <div className="absolute inset-x-0 top-0 h-1/4 bg-[linear-gradient(180deg,#fff_0%,rgba(255,255,255,0)_100%)]" data-id="element-7017" data-mp="w7rsu8l5x04k" />
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-[linear-gradient(0deg,#fff_0%,rgba(255,255,255,0.6)_50%,rgba(255,255,255,0)_100%)]" data-id="element-7018" data-mp="3uq731gnab371" />
        <div className="absolute inset-y-0 right-0 w-1/6 bg-[linear-gradient(270deg,rgba(255,255,255,0.6)_0%,rgba(255,255,255,0)_100%)]" data-id="element-7019" data-mp="3kfc4f1onmj6" />
      </div>

      {/* Connection arcs */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 820 520" aria-hidden="true" data-id="element-7020" data-mp="ch67ep8euxcp">
        <g fill="none" stroke="#B9B4F4" strokeWidth="1" data-id="element-7021" data-mp="1jwkmtzi84xwj">
          <path d="M218 50 C 260 40, 300 70, 320 130" data-id="element-7022" data-mp="1nxo50snqv60x" />
          <path d="M172 230 C 200 250, 215 260, 236 262" data-id="element-7023" data-mp="1o0adqvmdfrkq" />
          <path d="M112 370 C 150 380, 190 380, 230 360" data-id="element-7024" data-mp="38f6slafn6ycc" />
          <path d="M722 50 C 680 40, 640 70, 620 130" data-id="element-7025" data-mp="34f51fv7j9nbb" />
          <path d="M726 222 C 710 230, 700 236, 690 240" data-id="element-7026" data-mp="34foil9pd07oa" />
          <path d="M730 380 C 715 390, 705 392, 692 390" data-id="element-7027" data-mp="2znd7dap64zgf" />
        </g>
        <g fill="#8B84F0" data-id="element-7028" data-mp="2up3bsyzj7wua">
          <circle cx="218" cy="50" r="2.4" data-id="element-7029" data-mp="2upv7e9t4pukk" />
          <circle cx="722" cy="50" r="2.4" data-id="element-7030" data-mp="2uotlfaakrno2" />
          <circle cx="320" cy="130" r="2" data-id="element-7031" data-mp="2pqia0v4mt53q" />
          <circle cx="620" cy="130" r="2" data-id="element-7032" data-mp="2prjutrbg95yn" />
        </g>
      </svg>

      <div className="absolute left-[200px] top-[150px]" data-id="element-7033" data-mp="2kqmezldwwo0b">
        <TrustLaptop pick={pick} lang={lang} data-id="element-7034" data-mp="2kt8nbn8mn7jl" />
      </div>
      <div className="absolute left-[548px] top-[140px]" data-id="element-7035" data-mp="2gq3obunr9nzp">
        <TrustPhone pick={pick} data-id="element-7036" data-mp="2bqhabj03s3pu" />
      </div>

      {trustSignals.map(s => <div key={s.key} className="absolute" style={SIGNAL_POS[s.key]} data-id="element-7037" data-mp="2bsajp623p21j">
          <SignalCard k={s.key} label={pick(s.label)} data-id="element-7038" data-mp="exffn5sce8cj" />
        </div>)}
    </div>;
}
export function TrustHero() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-white px-6 pt-12 lg:pt-16 pb-16 lg:pb-20" aria-labelledby="trust-hero-title" data-id="element-7039" data-mp="32u4cpck70x3z">
      <div className="pointer-events-none absolute -top-40 -left-40 w-[640px] h-[460px] rounded-full bg-[#F1F0FD] opacity-70 blur-[130px]" aria-hidden="true" data-id="element-7040" data-mp="2tsmyy0u9d7ig" />
      <div className="pointer-events-none absolute top-10 right-0 w-[520px] h-[380px] rounded-full bg-[#EEF3FD] opacity-70 blur-[130px]" aria-hidden="true" data-id="element-7041" data-mp="2yq6er56ltsci" />

      <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 xl:grid-cols-[47fr_53fr] gap-10 xl:gap-4 items-center" data-id="element-7042" data-mp="2ou3e63rei1ro">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-7043" data-mp="2b3gcc2qcfvrd">
          <p className="font-sans text-[13px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-7044" data-mp="2150p4auft6n5">{pick(c.eyebrow)}</p>
          <h1 id="trust-hero-title" className="mt-6 font-serif text-[2.6rem] sm:text-[3.4rem] lg:text-[4rem] xl:text-[4.1rem] 2xl:text-[4.5rem] leading-[1.02] tracking-tight text-[#121A3A]" data-id="element-7045" data-mp="262ai20h1c0fq">
            <span className="block" data-id="element-7046" data-mp="1hjqnu55ac1q7">{pick(c.headline.line1)}</span>
            <span className="block" data-id="element-7047" data-mp="1wfq3c0uq545d">
              {pick(c.headline.line2)}
              <span className="text-[#4F46E5] whitespace-nowrap" data-id="element-7048" data-mp="1rgmyagequxxe">{pick(c.headline.accent)}</span>
            </span>
          </h1>
          <p className="mt-6 max-w-[600px] font-sans text-[17px] md:text-[19px] text-[#5A6178] leading-relaxed" data-id="element-7049" data-mp="25f33u40veidy">{pick(c.body)}</p>
          <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
          location: 'Trust Hero'
        })} className="mt-9 inline-flex w-full sm:w-auto items-center justify-center gap-4 rounded-md bg-[#4F46E5] px-9 py-4 font-sans text-[17px] font-medium text-white hover:bg-[#4338CA] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2" data-id="element-7050" data-mp="20grv5wmbadxb">
            {pick(c.cta)}
            <ArrowRightIcon className="w-5 h-5" aria-hidden="true" data-id="element-7051" data-mp="2r90eu31g41qh" />
          </Link>
        </motion.div>

        {/* Product composition — tablet & desktop */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.08,
        ease: EASE
      }} className="hidden md:block" data-id="element-7052" data-mp="2w4z741pwa3w6">
          <FitScale width={820} height={520} data-id="element-7053" data-mp="39zi766id8vbb">
            <HeroComposition pick={pick} lang={lang} data-id="element-7054" data-mp="3a0t6nu4s3gwm" />
          </FitScale>
        </motion.div>

        {/* Mobile — phone + simplified trust signals */}
        <div className="md:hidden" data-id="element-7055" data-mp="3jxxwq51qvwip">
          <div className="relative flex justify-center" data-id="element-7056" data-mp="3jxo7ijoixte2">
            <img src={TRUST_HERO_CITY} alt="" className="absolute inset-x-0 top-8 h-[260px] w-full object-cover rounded-2xl opacity-40" aria-hidden="true" data-id="element-7057" data-mp="3ow9325stzow6" />
            <div className="relative" data-id="element-7058" data-mp="1pl82tnbdmnbb">
              <TrustPhone pick={pick} data-id="element-7059" data-mp="1uh6mwypo9j5x" />
            </div>
          </div>
          <ul className="mt-6 grid grid-cols-2 gap-2" data-id="element-7060" data-mp="bmlksex39xow">
            {trustSignals.map(s => {
            const Icon = SIGNAL_ICONS[s.key];
            return <li key={s.key} className="flex items-center gap-2.5 rounded-xl border border-[#ECEBF7] bg-white px-3 py-2.5" data-id="element-7061" data-mp="6vbz1penveia">
                  <Icon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-7062" data-mp="2sfnujrgakod" />
                  <span className="font-sans text-[13px] text-[#121A3A] leading-tight" data-id="element-7063" data-mp="3tz091mutqydh">{pick(s.label)}</span>
                </li>;
          })}
          </ul>
        </div>
      </div>
    </section>;
}